import React, { useEffect, useState, useCallback } from 'react';
import {
  Wrench, CheckCircle2, XCircle, Clock, AlertTriangle, RefreshCw,
  Eye, User, Building2, Calendar, Image as ImageIcon, Plus, Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { repairRequestsAPI, propertiesAPI, clientsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ModuleTasksPanel from '../components/ModuleTasksPanel';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_BADGES = {
  en_attente: { label: 'En attente', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  validee: { label: 'Validee', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  rejetee: { label: 'Rejetee', cls: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
};

const PRIORITY_BADGES = {
  faible: { cls: 'bg-gray-100 text-gray-700' },
  normale: { cls: 'bg-blue-100 text-blue-800' },
  urgente: { cls: 'bg-red-100 text-red-700 font-bold' },
};

const fmtDate = (s) => {
  if (!s) return '-';
  try { return format(new Date(s), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch { return s; }
};

export default function RepairRequests() {
  const { user } = useAuth();
  const isAdmin = user && (['admin', 'super_admin', 'agent'].includes(user.role));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ request_types: [], priorities: [] });
  const [filterStatus, setFilterStatus] = useState('all');

  // Create modal (agent + admin)
  const [showCreate, setShowCreate] = useState(false);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState({
    property_id: '', tenant_id: '', request_type: 'plomberie',
    subject: '', description: '', priority: 'normale', photos: [],
  });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Detail / Approve / Reject modals
  const [detail, setDetail] = useState(null);
  const [showApprove, setShowApprove] = useState(false);
  const [approveForm, setApproveForm] = useState({
    artisan_name: '', artisan_phone: '', scheduled_date: '', estimated_cost: '', notes: '',
  });
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const r = await repairRequestsAPI.getAll(params);
      setItems(r.data || []);
    } catch (e) { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [filterStatus]);

  useEffect(() => {
    repairRequestsAPI.getMeta().then((r) => setMeta(r.data)).catch(() => {});
    propertiesAPI.getAll().then((r) => setProperties(r.data || [])).catch(() => {});
    clientsAPI.getAll({ client_type: 'locataire' }).then((r) => setTenants(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = {
    total: items.length,
    pending: items.filter((i) => i.status === 'en_attente').length,
    approved: items.filter((i) => i.status === 'validee').length,
    rejected: items.filter((i) => i.status === 'rejetee').length,
  };

  const handleSubmitCreate = async () => {
    if (!form.property_id || !form.subject.trim() || !form.description.trim()) {
      toast.error('Bien, sujet et description obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      // 1. Upload all photos first
      const urls = [];
      for (const f of photoFiles) {
        try {
          const r = await repairRequestsAPI.uploadPhoto(f);
          if (r.data?.url) urls.push(r.data.url);
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('photo upload failed', err);
        }
      }
      // 2. Create request
      await repairRequestsAPI.create({ ...form, photos: urls });
      toast.success('Demande envoyee');
      setShowCreate(false);
      setForm({ property_id: '', tenant_id: '', request_type: 'plomberie',
                subject: '', description: '', priority: 'normale', photos: [] });
      setPhotoFiles([]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await repairRequestsAPI.approve(detail.id, {
        ...approveForm,
        estimated_cost: approveForm.estimated_cost ? Number(approveForm.estimated_cost) : null,
      });
      toast.success('Demande validee, reparation creee');
      setShowApprove(false);
      setDetail(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSubmitting(false);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Motif obligatoire'); return; }
    setSubmitting(true);
    try {
      await repairRequestsAPI.reject(detail.id, rejectReason.trim());
      toast.success('Demande rejetee');
      setShowReject(false);
      setDetail(null);
      setRejectReason('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="repair-requests-page">
      <ModuleTasksPanel moduleKey="repair_requests" moduleLabel="Demandes Reparations" />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1B3A4B] flex items-center gap-2">
            <Wrench className="h-8 w-8 text-[#D4A745]" />
            Demandes de Reparation
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {isAdmin
              ? 'Validez ou rejetez les demandes envoyees par les locataires et agents.'
              : 'Soumettez une demande de reparation - validation par l\'administrateur.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}
                  className="border-[#1B3A4B] text-[#1B3A4B]"
                  data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
          <Button onClick={() => setShowCreate(true)}
                  className="bg-[#1B3A4B] hover:bg-[#0d2937] text-white"
                  data-testid="new-request-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle demande
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-gray-500">Total</div>
          <div className="text-2xl font-bold text-[#1B3A4B]">{stats.total}</div>
        </CardContent></Card>
        <Card className="border-amber-200"><CardContent className="p-4">
          <div className="text-xs uppercase text-amber-700">En attente</div>
          <div className="text-2xl font-bold text-amber-700">{stats.pending}</div>
        </CardContent></Card>
        <Card className="border-emerald-200"><CardContent className="p-4">
          <div className="text-xs uppercase text-emerald-700">Validees</div>
          <div className="text-2xl font-bold text-emerald-700">{stats.approved}</div>
        </CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4">
          <div className="text-xs uppercase text-red-700">Rejetees</div>
          <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
        </CardContent></Card>
      </div>

      {/* Filter */}
      <Card><CardContent className="p-3 flex items-center gap-3">
        <Label className="text-xs">Statut</Label>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-64" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="en_attente">En attente</SelectItem>
            <SelectItem value="validee">Validees</SelectItem>
            <SelectItem value="rejetee">Rejetees</SelectItem>
          </SelectContent>
        </Select>
      </CardContent></Card>

      {/* List */}
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1B3A4B] text-white">
              <tr>
                <th className="text-left p-3">N°</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Sujet</th>
                <th className="text-left p-3">Bien / Locataire</th>
                <th className="text-left p-3">Demandeur</th>
                <th className="text-center p-3">Priorite</th>
                <th className="text-center p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="p-8 text-center text-gray-500">
                  <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                </td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-gray-500">Aucune demande.</td></tr>
              )}
              {items.map((it) => {
                const Sb = STATUS_BADGES[it.status] || STATUS_BADGES.en_attente;
                const Pb = PRIORITY_BADGES[it.priority] || PRIORITY_BADGES.normale;
                const SIcon = Sb.icon;
                return (
                  <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`request-row-${it.id}`}>
                    <td className="p-3 font-mono text-xs">{it.request_number}</td>
                    <td className="p-3 text-xs">{fmtDate(it.created_at)}</td>
                    <td className="p-3">{it.request_type_label || it.request_type}</td>
                    <td className="p-3 max-w-xs truncate">{it.subject}</td>
                    <td className="p-3 text-xs">
                      <div>{it.property_title}</div>
                      <div className="text-gray-500">{it.tenant_name || '-'}</div>
                    </td>
                    <td className="p-3 text-xs">
                      <Badge variant="outline" className="text-xs">
                        {it.requested_by_role === 'tenant' ? 'Locataire' : it.requested_by_role === 'admin' ? 'Admin' : 'Agent'}
                      </Badge>
                      <div className="text-gray-500 mt-1">{it.requested_by_name}</div>
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={Pb.cls}>{it.priority_label || it.priority}</Badge>
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={`${Sb.cls} border`}>
                        <SIcon className="h-3 w-3 mr-1 inline" />{Sb.label}
                      </Badge>
                      {it.linked_repair_ref && (
                        <div className="text-[10px] text-emerald-700 mt-1 font-mono">
                          → {it.linked_repair_ref}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost"
                              onClick={() => setDetail(it)}
                              data-testid={`view-${it.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {isAdmin && it.status === 'en_attente' && (
                        <>
                          <Button size="sm" variant="ghost"
                                  onClick={() => { setDetail(it); setApproveForm({
                                    artisan_name: '', artisan_phone: '', scheduled_date: '',
                                    estimated_cost: it.estimated_cost || '', notes: '',
                                  }); setShowApprove(true); }}
                                  data-testid={`approve-${it.id}`}
                                  className="text-emerald-600 hover:bg-emerald-50">
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost"
                                  onClick={() => { setDetail(it); setRejectReason(''); setShowReject(true); }}
                                  data-testid={`reject-${it.id}`}
                                  className="text-red-600 hover:bg-red-50">
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B3A4B]">Nouvelle demande de reparation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bien *</Label>
                <Select value={form.property_id}
                        onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="property-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title || p.address}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Locataire (facultatif)</Label>
                <Select value={form.tenant_id || 'none'}
                        onValueChange={(v) => setForm({ ...form, tenant_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="tenant-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucun —</SelectItem>
                    {tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type de probleme *</Label>
                <Select value={form.request_type}
                        onValueChange={(v) => setForm({ ...form, request_type: v })}>
                  <SelectTrigger data-testid="type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.request_types?.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Urgence *</Label>
                <Select value={form.priority}
                        onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.priorities?.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Sujet *</Label>
              <Input value={form.subject}
                     onChange={(e) => setForm({ ...form, subject: e.target.value })}
                     placeholder="Ex: Fuite robinet cuisine"
                     data-testid="subject-input" />
            </div>
            <div>
              <Label>Description detaillee *</Label>
              <Textarea rows={3} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Decrivez le probleme avec un maximum de details"
                        data-testid="description-input" />
            </div>
            <div>
              <Label>Photos (facultatif - jusqu'a 5)</Label>
              <Input type="file" multiple accept="image/*"
                     onChange={(e) => setPhotoFiles(Array.from(e.target.files || []).slice(0, 5))}
                     data-testid="photos-input" />
              {photoFiles.length > 0 && (
                <div className="text-xs text-emerald-700 mt-1 flex items-center">
                  <ImageIcon className="h-3 w-3 mr-1" /> {photoFiles.length} photo(s) jointes
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleSubmitCreate} disabled={submitting}
                    className="bg-[#1B3A4B] hover:bg-[#0d2937] text-white"
                    data-testid="submit-create-btn">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={!!detail && !showApprove && !showReject} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl" data-testid="detail-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B3A4B]">{detail?.request_number}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Date</span><span>{fmtDate(detail.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Type</span><span>{detail.request_type_label}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Priorite</span>
                <Badge className={(PRIORITY_BADGES[detail.priority] || PRIORITY_BADGES.normale).cls}>{detail.priority_label}</Badge>
              </div>
              <div className="flex justify-between"><span className="text-gray-600">Bien</span><span>{detail.property_title}</span></div>
              {detail.tenant_name && <div className="flex justify-between"><span className="text-gray-600">Locataire</span><span>{detail.tenant_name}</span></div>}
              <div className="flex justify-between"><span className="text-gray-600">Demandeur</span>
                <span>{detail.requested_by_name} ({detail.requested_by_role})</span>
              </div>
              <div><div className="text-gray-600 text-xs uppercase mt-2">Sujet</div><div className="font-semibold">{detail.subject}</div></div>
              <div><div className="text-gray-600 text-xs uppercase">Description</div><div className="whitespace-pre-wrap">{detail.description}</div></div>
              {detail.photos?.length > 0 && (
                <div>
                  <div className="text-gray-600 text-xs uppercase mb-1">Photos</div>
                  <div className="grid grid-cols-3 gap-2">
                    {detail.photos.map((url, i) => (
                      <a key={url || `photo-${i}`} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noreferrer">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                             alt="" className="w-full h-24 object-cover rounded border" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-600">Statut</span>
                <Badge className={(STATUS_BADGES[detail.status] || STATUS_BADGES.en_attente).cls + ' border'}>
                  {(STATUS_BADGES[detail.status] || STATUS_BADGES.en_attente).label}
                </Badge>
              </div>
              {detail.linked_repair_ref && (
                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-2 rounded text-emerald-900">
                  <div className="text-xs">Reparation creee : <span className="font-mono font-semibold">{detail.linked_repair_ref}</span></div>
                  {detail.repair_artisan && <div className="text-xs">Artisan : {detail.repair_artisan}</div>}
                  {detail.repair_scheduled_date && <div className="text-xs">Prevue le {detail.repair_scheduled_date}</div>}
                </div>
              )}
              {detail.status === 'rejetee' && detail.rejection_reason && (
                <div className="bg-red-50 border-l-4 border-red-500 p-2 rounded text-red-900">
                  <div className="text-xs font-semibold">Motif de rejet</div>
                  <div className="text-xs">{detail.rejection_reason}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={showApprove} onOpenChange={setShowApprove}>
        <DialogContent className="max-w-lg" data-testid="approve-modal">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center">
              <CheckCircle2 className="h-5 w-5 mr-2" /> Valider la demande
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Une reparation sera creee automatiquement et le locataire sera notifie.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Artisan assigne</Label>
                <Input value={approveForm.artisan_name}
                       onChange={(e) => setApproveForm({ ...approveForm, artisan_name: e.target.value })}
                       placeholder="Nom de l'artisan"
                       data-testid="artisan-name-input" />
              </div>
              <div>
                <Label>Telephone artisan</Label>
                <Input value={approveForm.artisan_phone}
                       onChange={(e) => setApproveForm({ ...approveForm, artisan_phone: e.target.value })}
                       placeholder="+223..."
                       data-testid="artisan-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date prevue</Label>
                <Input type="date" value={approveForm.scheduled_date}
                       onChange={(e) => setApproveForm({ ...approveForm, scheduled_date: e.target.value })}
                       data-testid="scheduled-date-input" />
              </div>
              <div>
                <Label>Cout estime (FCFA)</Label>
                <Input type="number" value={approveForm.estimated_cost}
                       onChange={(e) => setApproveForm({ ...approveForm, estimated_cost: e.target.value })}
                       data-testid="estimated-cost-input" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={approveForm.notes}
                        onChange={(e) => setApproveForm({ ...approveForm, notes: e.target.value })}
                        data-testid="approve-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprove(false)}>Annuler</Button>
            <Button onClick={handleApprove} disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="confirm-approve-btn">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Valider et creer reparation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="max-w-md" data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center">
              <XCircle className="h-5 w-5 mr-2" /> Rejeter la demande
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motif du rejet *</Label>
            <Textarea rows={4} value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Ex: Probleme de la responsabilite du locataire selon le bail..."
                      data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>Annuler</Button>
            <Button onClick={handleReject} disabled={submitting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-reject-btn">
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
